import { Reveal } from "@components/ui";

const solutions = [
  {
    title: "Centralized Tank Systems",
    bullets: [
      "Underground or above-ground",
      "Automated tank monitoring",
      "Emergency cut-off system",
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <path
          d="M4 8h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8zM6 8V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2M12 12v4M10 14h4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Smart Metering Per Villa/Townhouse",
    bullets: [
      "Perfect for compounds",
      "No manual reading",
      "Transparent billing",
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <path
          d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M12 7v5l3 3M8 7h.01M16 7h.01"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Full O&M Services",
    bullets: [
      "Monthly inspections",
      "Tank level monitoring",
      "24/7 emergency support",
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <path
          d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

export function CommunitySolutionsSection() {
  return (
    <section className="w-full bg-white overflow-hidden py-10 md:py-14 lg:py-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-8 md:gap-12 lg:gap-16">
        {/* Header */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-3 md:gap-4 text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase font-albert-sans">
              Community Solutions
            </span>
            <h2 className="text-[24px] md:text-[40px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              Community Solutions for Compounds & Villas
            </h2>
            <p className="max-w-[966px] text-[14px] md:text-base lg:text-[24px] tracking-[-0.04em] leading-[1.4] text-[#62636c] font-albert-sans">
              For larger residential communities, GAZ offers comprehensive solutions
            </p>
          </div>
        </Reveal>

        {/* Solution Cards */}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {solutions.map((solution, index) => (
            <Reveal key={solution.title} direction="up" delay={index * 100} className="flex">
              <div className="flex-1 relative rounded-[12px] md:rounded-[16px] shadow-[0px_4px_24px_rgba(0,0,0,0.1)] bg-white border border-transparent hover:border-[#122d63]/15 hover:shadow-[0_8px_32px_rgba(18,45,99,0.12)] p-4 md:py-[22px] md:px-[23px] flex flex-col gap-4 md:gap-6 transition-all duration-300 overflow-hidden group">
                {/* Watermark Number */}
                <span className="absolute -right-2 -top-4 text-[100px] md:text-[120px] font-bold text-[#122d63]/[0.03] leading-none font-albert-sans select-none pointer-events-none group-hover:text-[#122d63]/[0.06] transition-colors duration-300">
                  {String(index + 1).padStart(2, "0")}
                </span>

                {/* Icon + Number Badge Row */}
                <div className="flex items-center gap-3 relative z-[1]">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] bg-[#122d63] flex items-center justify-center shrink-0">
                    <span className="text-[18px] md:text-[22px] font-semibold text-white font-albert-sans">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-[10px] bg-[#122d63]/8 text-[#122d63] flex items-center justify-center shrink-0">
                    {solution.icon}
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-col gap-2 md:gap-2.5 relative z-[1]">
                  <h3 className="text-[20px] md:text-[24px] lg:text-[28px] tracking-[-0.04em] font-semibold text-black font-albert-sans leading-[1.2]">
                    {solution.title}
                  </h3>
                  <div className="flex flex-col gap-2 md:gap-3">
                    {solution.bullets.map((bullet) => (
                      <div key={bullet} className="flex items-center gap-2.5">
                        <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-[#122d63]/8 flex items-center justify-center shrink-0">
                          <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3 md:w-3.5 md:h-3.5">
                            <path
                              d="M5 12l5 5L20 7"
                              stroke="#122d63"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <span className="text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] leading-[26px] text-[#62636c] font-albert-sans">
                          {bullet}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
